import { Refresh as RefreshIcon } from '@material-ui/icons';
import styled from 'styled-components';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { push, replace } from 'redux-first-history';
import { compose } from 'react-recompose';
import { flatten, identity, isEmpty, pickBy } from 'lodash-es';
import { Container, palette, SearchInput, SimpleLoader } from '@dev/ee-components';
import { DeliveryLogType, MessageCategory, AccessLevel } from '@dev/ee-api/ee-api-models';

import { addParamToUrl, parseUrlParams } from '@api/services/tools/handleUrlParams';
import { HideDialog, ShowDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import { LoadLogsStart, ResendStart } from '@api/store/logs/actions';
import { StdLoadLogsInterface } from '@api/store/logs/types';
import { LoadChannelsStart } from '@api/store/channels/actions';
import { ChannelsPayload } from '@api/store/channels/types';
import { ApplicationState as State } from '@api/store';
import { ViewEmailStart } from '@api/store/emails/actions';
import { debounce } from '@api/services/tools/debounce';
import { ResendParams } from '@api/services/ApiCategories/logs';
import { LogsTable } from '@api/components/Logs/assets/LogsTable';
import { DEFAULT_PER_PAGE_COUNT } from '@api/services/constants';
import { EventsFiltersTypeEnum } from '@api/components/Logs/Interface';
import { convertMessageCategoryAndStatuses, splitSelectedItemsIntoProperState } from '@api/components/Logs/helperFunctions';

import LogsInterface from '../LogsInterface';
import { inProgressStatusesArray } from '../config';
import { INITIAL_PER_PAGE } from '@api/store/exports/types';

const mapState = (state: State, ownProps: LogsInterface): LogsInterface => {
    return {
        ...ownProps,
        account: state.account,
        browser: state.browser,
        logs: state.logs,
        channels: state.channels,
    };
};

const dispatchProps = {
    LoadLogsStart,
    LoadChannelsStart,
    ViewEmailStart,
    ShowDialog,
    HideDialog,
    Push: push,
    Replace: replace,
    ResendStart,
};

type DispatchProps = {
    LoadLogsStart(params: StdLoadLogsInterface);
    LoadChannelsStart(params?: ChannelsPayload);
    ViewEmailStart(msgid: string);
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    Push(url: string): void;
    Replace(param: Record<string, string> | string): void;
    ResendStart: (params: ResendParams) => void;
};

type Props = DispatchProps & LogsInterface;
export interface SvgProps {
    onClick?(event: any): void;
    msgid?: string;
}

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 96px;
    width: 100%;
`;

type LogContainerType = {
    isMobile?: boolean;
};

const LogContainer = styled.div<LogContainerType>`
    border: 1px solid ${palette.gray[3]};
    border-radius: 4px;
    margin-top: 24px;
    padding: 32px 0 0 0;
    background-color: #fff;
`;

const LogMobileContainer = styled.div`
    padding: 32px 0 0;
`;

const MobileHeader = styled.div`
    display: flex;
    margin-bottom: 20px;
    justify-content: space-between;
`;

const MobileSearchContainer = styled.div`
    width: 100%;

    svg {
        fill: ${palette.gray[5]};
    }

    & > div > div {
        border: 1px solid ${palette.gray[3]};
    }
`;

const TopContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0 48px 24px;
`;

const StyledRefreshIcon = styled.div`
    margin-left: 28px;
    &:hover {
        cursor: pointer;
    }
`;

class StatisticsInProgressLog extends Component<Props> {
    public state = {
        statuses: null,
        selectedChannel: null,
        selectedResult: null,
        perPage: DEFAULT_PER_PAGE_COUNT,
        emailSearch: '',
        activePage: 1,
        onlyLastEvent: true,
    };

    public componentDidMount(): void {
        const { LoadChannelsStart, LoadLogsStart, account, logs } = this.props;
        const { perPage, selectedChannel, selectedResult, onlyLastEvent } = this.state;
        const params: Record<string, string | string[] | number | boolean> = {
            statuses: [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending],
            inProgressLogPage: 1,
            channelName: selectedChannel ?? null,
            messageCategory: selectedResult ? [selectedResult] : null,
            skipSagaURL: true,
            onlyLastEvent,
        };

        if (account?.GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ViewChannels)) {
            LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset: 0, skipSagaURL: true });
        }

        if (this.loadLogsFromUrl()) {
            return;
        }

        if (isEmpty(logs.logList)) {
            LoadLogsStart({ perPage, ...params });
        }
    }

    public shouldComponentUpdate(nextProps, nextState): boolean {
        const currTotalCount = this.props.logs.totalLogsCount;
        const nextTotalCount = nextProps.logs.totalLogsCount;
        const currRecipientCount = this.props.logs.logList?.Recipients.length;
        const nextRecipientCount = nextProps.logs.logList?.Recipients.length;
        const { activePage, perPage } = this.state;

        let refresh = true;
        if (
            currTotalCount === nextTotalCount &&
            activePage === nextState.activePage &&
            perPage === nextState.perPage &&
            currRecipientCount === nextRecipientCount
        ) {
            refresh = false;
        }

        return refresh;
    }

    private loadLogsFromUrl(): boolean {
        const { location } = this.props;
        const query = parseUrlParams(location.search);

        if (isEmpty(query)) {
            return false;
        }

        const urlStatuses: string[] = query.statuses ? flatten([query.statuses]) : null;
        const urlResult: string[] = query.result ? flatten([query.result]) : null;
        const valuesBasedOnQuery = {
            statuses: urlStatuses,
            selectedChannel: query?.channel,
            selectedResult: urlResult,
            emailSearch: query?.search,
            perPage: parseInt(query?.perPage as string),
            activePage: parseInt(query.page as string),
        };
        const result = pickBy(valuesBasedOnQuery, identity);
        this.setState({ ...result }, () => this.updateLogs());
        return true;
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private updateLogs(): void {
        const { LoadLogsStart } = this.props;
        const { selectedChannel, selectedResult, statuses, perPage, emailSearch, activePage, onlyLastEvent } = this.state;
        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(statuses, selectedResult, onlyLastEvent);
        const params = {
            statuses: isEmpty(convertedStatuses)
                ? [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending]
                : convertedStatuses,
            channelName: selectedChannel,
            messageCategory: isEmpty(selectedResult) ? null : convertedResults,
            inProgressLogPage: activePage || 1,
            perPage,
            email: emailSearch,
            skipSagaURL: true,
            onlyLastEvent,
        };

        LoadLogsStart(params);
    }

    private fetchNextOnScroll(logLoading = true): void {
        const { LoadLogsStart } = this.props;
        const { statuses, selectedChannel, selectedResult, emailSearch, activePage, perPage, onlyLastEvent } = this.state;
        const nextPage = activePage + 1;
        const params: StdLoadLogsInterface = {
            statuses: isEmpty(statuses) ? inProgressStatusesArray : statuses,
            inProgressLogPage: nextPage,
            channelName: selectedChannel ?? null,
            messageCategory: selectedResult ? [selectedResult] : null,
            email: emailSearch,
            skipSagaURL: true,
            perPage,
            logLoading,
            onlyLastEvent,
        };

        this.setState({ activePage: nextPage }, () => LoadLogsStart(params));
    }

    private onStatusFilterClick(event: React.SyntheticEvent<HTMLInputElement>, selectedItems: (DeliveryLogType | MessageCategory)[]): void {
        const [statuses, selectedResult] = splitSelectedItemsIntoProperState(selectedItems);

        this.addSearchQuery({ statuses, result: selectedResult });
        this.setState({ statuses, selectedResult, activePage: 1 }, (): void => {
            this.updateLogs();
        });
    }

    private renderTable(): JSX.Element {
        const { statuses, selectedResult } = this.state;

        const selectedCheckboxes = [...(statuses ?? []), ...(selectedResult ?? [])];

        return (
            <LogsTable
                fetchNextOnScroll={this.fetchNextOnScroll.bind(this, false)}
                onHeadersEventFilterClick={this.onStatusFilterClick.bind(this)}
                eventFiltersType={EventsFiltersTypeEnum.InProgress}
                selectedEventsCheckboxes={selectedCheckboxes}
            />
        );
    }

    private searchLogsWhereEmailStartWith(event): void {
        const emailSearch = event?.target?.value || '';
        this.setState({ emailSearch }, () => {
            if (emailSearch) {
                debounce(() => {
                    this.addSearchQuery({ search: emailSearch });
                    this.updateLogs();
                }, 1000);
            } else {
                this.updateLogs();
                this.addSearchQuery({ search: emailSearch });
            }
        });
    }

    private searchInput(): JSX.Element {
        const { t } = this.props;
        const { emailSearch } = this.state;
        const query = parseUrlParams(location.search);

        return (
            <SearchInput
                id="inProgressSearchInput"
                name="searchInput"
                defaultValue={query?.search || emailSearch}
                onChange={this.searchLogsWhereEmailStartWith.bind(this)}
                onDelete={this.searchLogsWhereEmailStartWith.bind(this)}
                placeholder={t('searchbyemail')}
                type="text"
            />
        );
    }

    private renderLoadingState(): JSX.Element {
        const displayContainer = {
            display: 'flex',
            height: 'calc(100vh - 200px)',
        };

        return (
            <Container>
                <Container style={displayContainer}>
                    <LoadingWrapper>
                        <SimpleLoader />
                    </LoadingWrapper>
                </Container>
            </Container>
        );
    }

    private isMobile(): boolean {
        return this.props?.browser?.lessThan?.hd;
    }

    public render(): JSX.Element {
        const { logs } = this.props;
        const isMobile = this.isMobile();

        if (logs.inProgressLoading) {
            return this.renderLoadingState();
        }

        if (isMobile) {
            return (
                <LogMobileContainer>
                    <MobileHeader>
                        <MobileSearchContainer>{this.searchInput()}</MobileSearchContainer>
                    </MobileHeader>
                    {this.renderTable()}
                </LogMobileContainer>
            );
        } else {
            return (
                <LogContainer>
                    <TopContainer>
                        {this.searchInput()}
                        <StyledRefreshIcon>
                            <RefreshIcon
                                onClick={() => {
                                    this.updateLogs();
                                }}
                            />
                        </StyledRefreshIcon>
                    </TopContainer>
                    {this.renderTable()}
                </LogContainer>
            );
        }
    }
}

/*

FIXME: init of refactor to FC
const InProgessLoading: FC = (): JSX.Element => {
    const displayContainer = {
        display: 'flex',
        height: 'calc(100vh - 200px)',
    };

    return (
        <Container>
            <Container style={displayContainer}>
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            </Container>
        </Container>
    );
}

const getParamsFromUrl = (location: Location) => {
    const query = parseUrlParams(location.search);

    if (isEmpty(query)) { return }

    const urlStatuses: string[] = query.statuses ? flatten([query.statuses]) : null;
    const urlResult: string[] = query.result ? flatten([query.result]) : null;
    const valuesBasedOnQuery = {
        statuses: urlStatuses,
        selectedChannel: query?.channel,
        selectedResult: urlResult,
        emailSearch: query?.search,
        perPage: parseInt(query?.perPage as string),
        activePage: parseInt(query.page as string),
    };
    return pickBy(valuesBasedOnQuery, identity);
}

const useSearchQuery = (toAdd: Record<string, any>): void => {
    const dispatch = useDispatch();
    const newPath = addParamToUrl(toAdd);
    dispatch(replace({ search: newPath }));
}

const InProgressSearchInput: FC = (): JSX.Element => {
    const { t } = useTranslation()
    const [ emailSearch, setEmailSearch] = useState();
    const query = parseUrlParams(location.search);

    const searchLogsWhereEmailStartWith = (event?): void  => {
        const emailSearchValue = event?.target?.value || '';
        this.setState({ emailSearch }, () => {
            if (emailSearch) {
                debounce(() => {
                    this.addSearchQuery({ search: emailSearch });
                    this.updateLogs();
                }, 1000);
            } else {
                this.updateLogs();
                this.addSearchQuery({ search: emailSearch });
            }
        });
    }

    return (
        <SearchInput
            id={"inProgressSearchInput" as string}
            name="searchInput"
            defaultValue={query?.search || emailSearch}
            onChange={searchLogsWhereEmailStartWith}
            onDelete={searchLogsWhereEmailStartWith}
            placeholder={t('searchbyemail')}
            type="text"
        />
    );
}

const InProgressLog: FC = (): JSX.Element => {
    const isMobile = useMobile();
    const isLoading = useSelector(getInProgressLoading)

    if (isLoading) {
        return (
            <>
                <InProgessLoading />
            </>
        )
    }

    if (isMobile) {
        return (
            <LogMobileContainer>
                <MobileHeader>
                    <MobileSearchContainer>
                        <InProgressSearchInput />
                    </MobileSearchContainer>
                </MobileHeader>
                {this.renderTable()}
            </LogMobileContainer>
        );
    }
}
*/

export default compose(withTranslation(), connect<LogsInterface, DispatchProps>(mapState, dispatchProps))(StatisticsInProgressLog);
